import { startOfMonth } from 'date-fns'
import { isEmpty, uniqBy } from 'lodash'

import {
  CACHE_TAG_WORK_ORDER_LIST_SUMMARY,
  CACHE_TAG_WORK_ORDER_LIST_SUMMARY_CS_SECTION,
  CACHE_TAG_WORK_ORDER_LIST_SUMMARY_WO_SECTION,
  CACHE_TAG_WORK_ORDER_LIST,
  CACHE_TAG_WORK_ORDER_LIST_CS_SECTION,
  CACHE_TAG_WORK_ORDER_LIST_WO_SECTION,
  CACHE_TAG_WORK_ORDER_REVIEW_LIST,
} from 'api/cacheTagTypes'
import { UNASSIGNED_ROUTE } from 'containers/new-route-manager/settings'
import { MONTHS } from 'settings/constants/date'
import { WORK_ORDER_STATUS } from 'settings/constants/service'
import { createDateFromString, formatDateToBEFormat } from 'utils/date'
import { get } from 'utils/lodash'

import { WORK_ORDER_STATUS_FILTER_MENU_OPTIONS } from './filters/work-order-status-filter/settings'

const { SERVICED, SKIPPED, MIXED, INCOMPLETE, MIXED_INCOMPLETE, PARTIALLY_SERVICED, PARTIALLY_SKIPPED } = WORK_ORDER_STATUS

/** @typedef {{id: string, value?: string}} Opt */

export const transformWorkOrderResponse = data => {
  const workOrders = get(data, 'workOrders', [])

  return workOrders.map(workOrder => ({
    ...workOrder,
    ...(!workOrder?.routeId && { routeId: UNASSIGNED_ROUTE.id, routeName: UNASSIGNED_ROUTE.name }),
  }))
}

export const groupDataByYearAndMonth = (data, key) => {
  //   const sortedData = sortByDateSelector({ data, key })
  const groupedData = new Map()
  data.forEach(row => {
    const date = createDateFromString(row[key])
    const year = date.getFullYear()
    const month = date.getMonth()

    if (!groupedData.has(year)) {
      groupedData.set(year, new Map())
    }
    if (!groupedData.get(year).has(month)) {
      groupedData.get(year).set(month, [])
    }

    groupedData.get(year).get(month).push(row)
  })

  return groupedData
}

export const getRouteOptions = allWorkOrders => {
  const filteredWOByRoutes = allWorkOrders.filter(wo => wo.routeId)
  const uniqueWOByRoutes = uniqBy(filteredWOByRoutes, 'routeId')
  const routesOptions = uniqueWOByRoutes.map(row => ({
    id: row.routeId,
    value: get(row, 'routeName', ''),
    routeColor: get(row, 'routeColor', ''),
  }))
  const hasUnassigned = allWorkOrders.some(wo => !wo.routeId)
  return hasUnassigned ? [{ id: UNASSIGNED_ROUTE.id, value: UNASSIGNED_ROUTE.name }, ...routesOptions] : [...routesOptions]
}

/**
 * A selection that covers every option is not a filter — omitting it keeps the request identical to
 * the unfiltered one, which is both cheaper on the database and a cache hit in RTK Query.
 */
export const isEverythingSelected = (selected, optionCount) => !selected?.length || selected.length >= optionCount

/**
 * Service ids are priced-service keyed — as configuredServiceIds they match nothing.
 * @param {{selectedServices?: Opt[], servicesOptions?: Opt[], selectedRoutes?: Opt[], routesOptions?: Opt[], selectedWorkOrderStatuses?: Opt[]}} selection
 */
export const buildWorkOrderListFilters = ({
  selectedServices = [],
  servicesOptions = [],
  selectedRoutes = [],
  routesOptions = [],
  selectedWorkOrderStatuses = [],
}) => ({
  ...(!isEverythingSelected(selectedWorkOrderStatuses, WORK_ORDER_STATUS_FILTER_MENU_OPTIONS.length) && {
    statuses: selectedWorkOrderStatuses.map(status => status.id),
  }),
  ...(!isEverythingSelected(selectedServices, servicesOptions?.length ?? 0) && {
    pricedServiceIds: selectedServices.map(service => service.id),
  }),
  ...(!isEverythingSelected(selectedRoutes, routesOptions?.length ?? 0) && {
    routeIds: selectedRoutes.filter(route => route.id !== UNASSIGNED_ROUTE.id).map(route => route.id),
    includeUnassigned: selectedRoutes.some(route => route.id === UNASSIGNED_ROUTE.id),
  }),
})

/**
 * Groups `byDate`-style periods `[{year, month, count}]` into `{year: [monthNumber, ...]}`, each
 * year's months sorted most-recent-first, for the Services tab's year/month accordion.
 */
export const groupPeriodsByYear = periods => {
  const grouped = {}
  periods.forEach(period => {
    const year = `${period.year}`
    if (!grouped[year]) grouped[year] = []
    grouped[year].push(period.month)
  })
  Object.keys(grouped).forEach(year => {
    grouped[year].sort((a, b) => b - a)
  })
  return grouped
}

export const getCurrentYearAndMonthFromPeriods = periods => {
  if (!periods.length) return { year: '', month: '' }
  const now = new Date()
  const currentYear = `${now.getFullYear()}`
  const currentMonthNumber = now.getMonth() + 1
  const match = periods.find(period => `${period.year}` === currentYear && period.month === currentMonthNumber)
  if (!match) return { year: '', month: '' }
  return { year: currentYear, month: `${currentYear}-${MONTHS[match.month - 1]}` }
}

export const getLatestYearAndMonthFromPeriods = periods => {
  if (!periods.length) return { year: '', month: '' }
  const [latest] = [...periods].sort((a, b) => b.year - a.year || b.month - a.month)
  return { year: `${latest.year}`, month: `${latest.year}-${MONTHS[latest.month - 1]}` }
}

export const getCanSeeMoreDetails = workOrderStatus =>
  [SERVICED, SKIPPED, MIXED, INCOMPLETE, MIXED_INCOMPLETE, PARTIALLY_SERVICED, PARTIALLY_SKIPPED].includes(workOrderStatus)

export const getAllWorkOrdersFromSummary = summary => {
  if (isEmpty(summary)) return []
  const allYears = Object.keys(summary)
  const allWorkOrders = []
  allYears.forEach(year => {
    const yearData = get(summary, year)
    const months = Object.keys(yearData)
    months.forEach(month => {
      const monthData = get(yearData, month)
      allWorkOrders.push(...monthData)
    })
  })
  return allWorkOrders
}

export const getWorkOrderListSummaryProvidesTags = payload => {
  const isWOSection = payload?.accountId || payload?.locationId
  return isWOSection
    ? [CACHE_TAG_WORK_ORDER_LIST_SUMMARY, CACHE_TAG_WORK_ORDER_LIST_SUMMARY_WO_SECTION]
    : [
        CACHE_TAG_WORK_ORDER_LIST_SUMMARY,
        CACHE_TAG_WORK_ORDER_LIST_SUMMARY_CS_SECTION,
        `${CACHE_TAG_WORK_ORDER_LIST_SUMMARY_CS_SECTION}-${get(payload, 'configuredServiceIds.0')}`,
      ]
}

export const getWorkOrderListProvidesTags = payload => {
  if (payload?.skipCache) {
    return []
  }

  if (payload?.startDate) {
    const isWOSection = payload?.accountId || payload?.locationId
    const commonDateTag = `${CACHE_TAG_WORK_ORDER_LIST}-${payload.startDate}`

    if (isWOSection) {
      return [
        CACHE_TAG_WORK_ORDER_LIST,
        CACHE_TAG_WORK_ORDER_LIST_WO_SECTION,
        commonDateTag,
        `${CACHE_TAG_WORK_ORDER_LIST_WO_SECTION}-${payload.startDate}`,
      ]
    }

    const isCSSection = get(payload, 'configuredServiceIds.length', 0) > 0
    if (isCSSection) {
      return [
        CACHE_TAG_WORK_ORDER_LIST,
        CACHE_TAG_WORK_ORDER_LIST_CS_SECTION,
        `${CACHE_TAG_WORK_ORDER_LIST_CS_SECTION}-${get(payload, 'configuredServiceIds.0')}`,
        commonDateTag,
        `${CACHE_TAG_WORK_ORDER_LIST_CS_SECTION}-${payload.startDate}`,
        `${CACHE_TAG_WORK_ORDER_LIST_CS_SECTION}-${get(payload, 'configuredServiceIds.0')}-${payload.startDate}`,
      ]
    }

    const isWorkOrderReviewSection = get(payload, 'invoiceStatuses.length', 0) > 0
    if (isWorkOrderReviewSection) {
      return [
        CACHE_TAG_WORK_ORDER_LIST,
        CACHE_TAG_WORK_ORDER_REVIEW_LIST,
        `${CACHE_TAG_WORK_ORDER_REVIEW_LIST}-${get(payload, 'invoiceStatuses.0')}`,
      ]
    }
  }

  return [CACHE_TAG_WORK_ORDER_LIST]
}

export const getWorkOrderListInvalidatesTags = ({ isNew, serviceDate, woSectionOnly, sectionWise, csId }) => {
  if (!isNew) {
    return [CACHE_TAG_WORK_ORDER_LIST]
  }

  if (serviceDate) {
    const startDate = startOfMonth(serviceDate instanceof Date ? serviceDate : createDateFromString(serviceDate))
    const formattedDate = formatDateToBEFormat(startDate)
    return csId
      ? // update work orders in the work order section and only the configured service section(by csId) for the given month
        [`${CACHE_TAG_WORK_ORDER_LIST_WO_SECTION}-${formattedDate}`, `${CACHE_TAG_WORK_ORDER_LIST_CS_SECTION}-${csId}-${formattedDate}`]
      : // update work orders in the work order section and all configured service sections for the given month
        [`${CACHE_TAG_WORK_ORDER_LIST}-${formattedDate}`]
  }

  if (woSectionOnly) {
    // Update work order section only
    return [CACHE_TAG_WORK_ORDER_LIST_SUMMARY_WO_SECTION]
  }

  if (sectionWise) {
    return csId
      ? // Update work order section and only the configured service section for the given csId
        [
          CACHE_TAG_WORK_ORDER_LIST_SUMMARY_WO_SECTION,
          `${CACHE_TAG_WORK_ORDER_LIST_SUMMARY_CS_SECTION}-${csId}`,
          `${CACHE_TAG_WORK_ORDER_LIST_CS_SECTION}-${csId}`,
        ]
      : // Update work order section and all configured service sections
        [CACHE_TAG_WORK_ORDER_LIST_SUMMARY, CACHE_TAG_WORK_ORDER_LIST_CS_SECTION]
  }

  return [CACHE_TAG_WORK_ORDER_LIST]
}

export const getWorkOrderPriceCents = row => {
  const { servicePriceFeeAndDisposalTicketTotalCents, feeTotalCents, disposalTicketTotalCents } = row
  // Show service price only, applicable for On Request and Instance pricing period services
  return servicePriceFeeAndDisposalTicketTotalCents - feeTotalCents - disposalTicketTotalCents
}

export const getActiveDisposalTickets = (disposalTickets = []) => disposalTickets.filter(({ deleted }) => !deleted)
